import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { Dot } from "../UIElements/svgIcons";
import { headerSty, contentSty } from "../table-styles";
import { formatDecimalNumber, formatTime, getUrlPathId, limitToSeven } from "../../utils/url-id";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { apiGetMedicIdTimesheet } from "../../api/medic";
import { useNotificationService } from "../notification";
import { MedicTimesheetInfo } from "../../interface/medic";
import { TimesheetModal } from "./modal-contents";

const rows = [
  {
    week: "A0B1C062",
    shift: "Morning Shift",
    time: "8:00am - 12:00pm",
    status: "pending",
    remark: "Clocked-in",
  },
  {
    week: "A0B1C062",
    shift: "Morning Shift ",
    time: "8:00am - 12:00pm",
    status: "absent",
    remark: "Not Present",
  },
  {
    week: "A0B1C062",
    shift: "Afternoon Shift",
    time: "12:00pm - 6:00pm",
    status: "swapped",
    remark: "Swapped with Dr. Odumejie",
  },
  {
    week: "A0B1C062",
    shift: "Afternoon Shift ",
    time: "12:00pm - 6:00pm",
    status: "completed",
    remark: "Completed",
  },
  {
    week: "A0B1C062",
    shift: "Afternoon Shift ",
    time: "12:00pm - 6:00pm",
    status: "completed",
    remark: "Completed",
  },
];

export default function MedicPersonnelShift() {
  const theme = useTheme();
  const headStyles = { ...headerSty, color: "#93A5D8", fontWeight: 400 };
  const contentStyles = { ...contentSty };
  const location = useLocation();
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const [timesheet, setTimesheet] = React.useState<MedicTimesheetInfo[]>([]);
    //
    const [selectedData, setSelectedData] = React.useState<{
      data: any;
      modal: boolean;
    }>({ data: {}, modal: false });

  React.useEffect(() => {
    const medicId = getUrlPathId(location?.pathname);
    const fields = { medicId: medicId, token: token };
    mutation.mutate(fields);
  }, []);

  const mutation = useMutation({
    mutationFn: (fields: { medicId: string; token: string }) =>
      apiGetMedicIdTimesheet(fields),
    onSuccess: (data) => {
      
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setTimesheet(data?.data?.data);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  const handleCloseSelectedModalData = () => {
    setSelectedData({ data: {}, modal: false });
  };
  const handleOpenSelectedModalData = (data: MedicTimesheetInfo) => {
    setSelectedData({ data: data, modal: true });
  };
  return (
    <>
    
  <TimesheetModal 
   open={selectedData.modal}
   timesheetData={selectedData.data}
   modalHandler={handleCloseSelectedModalData}
 
  />
    
    <TableContainer
      component={Paper}
      sx={{
        px: 2,
        py: 3,
        width: "100%",
        mx: "auto",
        minHeight: 300,
        background: "#FAFBFC",
        borderRadius: "16px",
        boxShadow: "none",
      }}
    >
      <Table
        sx={{ width: "98%", mx: "auto" }}
        size="small"
        aria-label="a dense table"
      >
        <TableHead>
          <TableRow>
          <TableCell sx={{ ...headStyles, }}>
              ID
            </TableCell>
            <TableCell sx={{ ...headStyles, }}>
              Schedule
            </TableCell>
            <TableCell sx={{ ...headStyles, }}>
              Shift
            </TableCell>
            <TableCell sx={{ ...headStyles, }}>
              Date Created
            </TableCell>
            <TableCell sx={{ ...headStyles, }}>
              Status{" "}
            </TableCell>
            <TableCell sx={{ ...headStyles, }}>
              Total Hours{" "}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!mutation.isPending && timesheet.map((row:MedicTimesheetInfo ) => (
            <TableRow
              key={row._id}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                sx={{ ...contentStyles, textAlign: "left", padding: "12px" }}
                component="th"
                scope="row"
              >
                {limitToSeven(row._id) ?? "-"}
              </TableCell>
              <TableCell
                sx={{ ...contentStyles, padding: "12px" }}
              >
                {row?.shift_info?.schedule_name}
              </TableCell>
              <TableCell
                sx={{ ...contentStyles, padding: "12px" }}
              >
                {formatTime(row?.shift_info?.start_time)} {"-"} {formatTime(row?.shift_info?.end_time)}
              </TableCell>
              <TableCell
                sx={{ ...contentStyles, padding: "12px" }}
              >
                {row?.shift_info?.schedule_name}
              </TableCell>
              <TableCell
                sx={{ ...contentStyles, padding: "12px" }}
              >
                <EmpStatus>{row.status}</EmpStatus>
              </TableCell>
              <TableCell
                sx={{ ...contentStyles,  padding: "12px" }}
              >
                {formatDecimalNumber(row?.overtime_hours) ?? "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {timesheet?.length === 0 && !mutation.isPending && (
        <Box
          sx={{
            height: 200,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, color: theme.palette.neutral.main }}
          >
            No Information on this medic
          </Typography>
        </Box>
      )}
      {mutation.isPending && (
        <Box sx={{ width: "96%", mx: "auto" }}>
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={"100%"}
            height={60}
            sx={{ my: 2 }}
          />
        </Box>
      )}
    </TableContainer>
    </>
  );
}

const EmpStatus: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        margin: "10px",
        paddingY: 1,
        paddingX: 2,
        borderRadius: "3px",
        color: theme.palette.primary.main,
        textAlign: "center",
        width: 60,
        backgroundColor: theme.palette.lightBlue.main,
      }}
    >
      {children}
    </Box>
  );
};
